import type { ChronologySettingsViewModel } from './chronology.js';
import {
  LOCAL_CHART_NAMESPACE,
  type LocalChartRepository,
} from './localPersistence.js';
import {
  assertRuntimeNetworkPolicy,
  DEFAULT_RUNTIME_NETWORK_POLICY,
  type RuntimeNetworkPolicy,
} from './runtimeNetworkPolicy.js';

export type DataManagementReadiness = 'ready' | 'attention';

export interface LocalStoragePrivacyViewModel {
  namespace: string;
  validChartCount: number;
  unsupportedVersionCount: number;
  invalidRecordCount: number;
  preservedProblemCount: number;
  status: DataManagementReadiness;
  disclosure: string;
}

export interface LocalTransferActionViewModel {
  id: 'private_backup' | 'redacted_share';
  label: string;
  badge: '私密備份' | '隱私分享';
  restorable: boolean;
  containsSensitiveBirthData: boolean;
  containsChartFingerprints: boolean;
  networkRequired: false;
  disclosure: string;
}

export interface RuntimePrivacyViewModel {
  policyId: string;
  policyVersion: string;
  status: 'local_only';
  outboundConnections: 'none';
  birthDataLeavesDevice: false;
  analytics: false;
  advertising: false;
  aiRuntime: false;
  disclosure: string;
}

export interface ChronologyPrivacyViewModel {
  selectedMode: 'standard' | 'extended';
  badge: '正式' | '研究' | '未啟用';
  canCalculate: boolean;
  disclosure: string;
}

export interface PrivacyDataManagementViewModel {
  readiness: DataManagementReadiness;
  storage: LocalStoragePrivacyViewModel;
  transferActions: readonly LocalTransferActionViewModel[];
  runtime: RuntimePrivacyViewModel;
  chronology?: ChronologyPrivacyViewModel;
  disclosure: string;
}

const TRANSFER_ACTIONS: readonly LocalTransferActionViewModel[] = Object.freeze([
  Object.freeze({
    id: 'private_backup' as const,
    label: '匯出私密備份',
    badge: '私密備份' as const,
    restorable: true,
    containsSensitiveBirthData: true,
    containsChartFingerprints: true,
    networkRequired: false as const,
    disclosure: '可重新匯入，包含完整出生資料與命盤內容；只應保存於你信任的裝置或儲存空間。',
  }),
  Object.freeze({
    id: 'redacted_share' as const,
    label: '匯出隱私分享版',
    badge: '隱私分享' as const,
    restorable: false,
    containsSensitiveBirthData: false,
    containsChartFingerprints: false,
    networkRequired: false as const,
    disclosure: '移除出生日期、時辰、性別、別名與 fingerprint；適合分享命盤摘要，但不能用來還原本機資料。',
  }),
]);

function chronologyPrivacyViewModel(settings: ChronologySettingsViewModel): ChronologyPrivacyViewModel {
  const selected = settings.options.find((option) => option.mode === settings.selectedMode);
  if (!selected) throw new Error(`Chronology settings missing selected mode ${settings.selectedMode}`);
  return Object.freeze({
    selectedMode: settings.selectedMode,
    badge: selected.badge,
    canCalculate: settings.canCalculate,
    disclosure: settings.disclosure,
  });
}

export function buildPrivacyDataManagementViewModel(
  repository: LocalChartRepository,
  chronology?: ChronologySettingsViewModel,
  networkPolicy: RuntimeNetworkPolicy = DEFAULT_RUNTIME_NETWORK_POLICY,
): PrivacyDataManagementViewModel {
  assertRuntimeNetworkPolicy(networkPolicy);
  const diagnostics = repository.diagnostics();
  const preservedProblemCount = diagnostics.invalidCount + diagnostics.unsupportedVersionCount;
  const readiness: DataManagementReadiness = preservedProblemCount === 0 ? 'ready' : 'attention';

  const storage: LocalStoragePrivacyViewModel = Object.freeze({
    namespace: LOCAL_CHART_NAMESPACE,
    validChartCount: diagnostics.validCount,
    unsupportedVersionCount: diagnostics.unsupportedVersionCount,
    invalidRecordCount: diagnostics.invalidCount,
    preservedProblemCount,
    status: readiness,
    disclosure: preservedProblemCount === 0
      ? '已保存命盤只存在此瀏覽器的專用本機 namespace；網站不會自動上傳。'
      : '偵測到無法讀取或版本不支援的舊紀錄；系統保留原資料供處理，不會自動刪除或改寫。',
  });

  const runtime: RuntimePrivacyViewModel = Object.freeze({
    policyId: networkPolicy.id,
    policyVersion: networkPolicy.version,
    status: 'local_only',
    outboundConnections: 'none',
    birthDataLeavesDevice: false,
    analytics: false,
    advertising: false,
    aiRuntime: false,
    disclosure: networkPolicy.disclosure,
  });

  return Object.freeze({
    readiness,
    storage,
    transferActions: TRANSFER_ACTIONS,
    runtime,
    ...(chronology ? { chronology: chronologyPrivacyViewModel(chronology) } : {}),
    disclosure: '核心排盤、解讀、本機保存、備份與資料管理都在瀏覽器本機完成；正式模式不需要帳號、Analytics、廣告、AI Runtime 或 Backend。',
  });
}
